import { z } from 'zod';
import type { CommunityType } from './community';

/**
 * Emergency DECLARATION + "Notify the PEOC" (Declaration & Notification Slice 1,
 * #495, migration 0124).
 *
 * "declared" / "declaration of emergency" is the RESERVED statutory term
 * (docs/ontario-ims-doctrine-reference.md §2.1 — a declaration of emergency under
 * the Emergency Management and Civil Protection Act, made by a head of council or
 * the Lieutenant Governor in Council). This is its CORRECT home: an operator-authored
 * record the COP captures ON BEHALF OF the declaring authority — records-not-confers,
 * the COP coordinates the record; it does NOT legally issue the declaration.
 *
 * LEGAL MODEL (encoded so a generic COP can't get it wrong):
 *  - The declaration DECLARER is the Head of Council (municipal). This is DISTINCT
 *    from the EVACUATION issuing-authority (Fire Chief / Medical Officer of Health /
 *    Minister) — never fold "mayor by declaration" into the evac authority picker.
 *  - A declaration, an evacuation order, and a public alert are PARALLEL SIBLINGS.
 *    A declaration is NOT required to order an evacuation; do not model it as a parent.
 *  - BOTH paths are wired: municipal (Head of Council, Slice 1) and first_nation
 *    (Band Council by BCR, Slice 3 / migration 0126 — see the Slice-3 block below).
 */

const UUID = z.string().uuid();

/** The KIND of declaring authority. 'first_nation' (BCR) UI path = Declaration Slice 3;
 *  'provincial' (EMCPA s.7.0.1, LGIC/Premier by Order in Council) = the RFI-upgrade slice
 *  (#614). Provincial is NOT tied to a community_type (authorityKindForCommunityType never
 *  returns it) — it is a DISTINCT affordance, not a roster-derived kind. */
export const DECLARATION_AUTHORITY_KINDS = ['municipal', 'first_nation', 'provincial'] as const;
export const DeclarationAuthorityKindSchema = z.enum(DECLARATION_AUTHORITY_KINDS);
export type DeclarationAuthorityKind = z.infer<typeof DeclarationAuthorityKindSchema>;

/** Operator-facing labels for the declaring-authority-kind. 'Province of Ontario' is a REAL
 *  jurisdiction (demo-seed fidelity) — the default provincial declaring_authority_name. */
export const DECLARATION_AUTHORITY_KIND_LABELS: Record<DeclarationAuthorityKind, string> = {
  municipal: 'Municipality',
  first_nation: 'First Nation',
  provincial: 'Province of Ontario',
};

/** The named-declarer ROLE per authority_kind (rendered on the surface + PDF). Municipal:
 *  the Head of Council. (first_nation: the Band Council via BCR — Slice 3.) provincial: the
 *  Lieutenant Governor in Council or the Premier — VERBATIM EMCPA s.7.0.1(1) (e-laws 90e09;
 *  doctrine-reference §2.1). */
export const DECLARATION_DECLARER_ROLE: Record<DeclarationAuthorityKind, string> = {
  municipal: 'Head of Council',
  first_nation: 'Band Council (by Band Council Resolution)',
  provincial: 'Lieutenant Governor in Council or the Premier',
};

/**
 * #521 — map a picked jurisdiction's `community_type` to its DECLARATION authority_kind,
 * so the declaring-authority PICKER (bound to the real FN/municipal roster) can retire the
 * fat-fingerable free-text and set the kind from the CHOSEN jurisdiction (never a manual guess).
 *
 * The declaration model has exactly TWO statutory paths:
 *  - first_nation → the Band Council declares BY BAND COUNCIL RESOLUTION (PERP §6.6.4; Indian
 *    Act s.2(1)/s.2(3)(b) — the council acts collectively, never a single head). This is the
 *    ONLY community_type that maps to the FN/BCR path.
 *  - municipality → the Head of Council declares under the EMCPA (§2.1). The municipal path.
 *
 * Every OTHER community_type (unincorporated / metis_settlement / inuit_community / other) has
 * NO clean single-path fit in the two-kind model, so it returns `null` — the caller keeps such
 * a jurisdiction OUT of the derived-kind picker and routes it through the narrow free-text
 * "other" fallback, where the operator picks the declaration path explicitly. This makes it
 * STRUCTURALLY impossible for the picker to mislabel a First Nation as municipal.
 */
export function authorityKindForCommunityType(
  communityType: CommunityType,
): DeclarationAuthorityKind | null {
  switch (communityType) {
    case 'first_nation':
      return 'first_nation';
    case 'municipality':
      return 'municipal';
    default:
      return null;
  }
}

/* ================================================================== *
 * PROVINCIAL declaration (#614, migration 0149) — EMCPA s.7.0.1.
 *
 * GOVERNING SOURCE (records-not-confers throughout): EMCPA s.7.0.1(1) (in-force EMCPA,
 * 2006, c. 13, s. 1(4) — NOT Bill 25), verbatim (e-laws 90e09; doctrine-reference §2.1):
 * "the Lieutenant Governor in Council or the Premier, if in the Premier's opinion the
 * urgency of the situation requires that an order be made immediately, may by order declare
 * that an emergency exists throughout Ontario or in any part of Ontario." It is declared BY
 * ORDER — never by an operator; the COP RECORDS it on behalf of the Province (records-not-
 * confers). A DISTINCT instrument from the municipal (s.4) / FN (BCR) declarations: NO PEOC
 * notify leg (the province is the provincial level), NO municipal s.4(3)/(6) copy, NO
 * umbrella over municipal declarations (parallel statutory acts). The provincial closeout is
 * a SEPARATE instrument (s.7.0.10, Premier → the Assembly within 120 days) — shipped as #616
 * (see the PROVINCIAL closeout block below). These strings are statutory-verbatim → they clear
 * the UI bar with NO §4.3 exception; pinned by emergency-declaration.lock.test.ts.
 * ================================================================== */

/** The two provincial declarer sub-choices (EMCPA s.7.0.1(1)) — written to declarer_name so the
 *  record names the SPECIFIC declarer under the generic role. NOT a bare "Governor" anywhere
 *  (US-federal drift) — "Lieutenant Governor in Council" is the correct Ontario term. */
export const PROVINCIAL_DECLARER_LGIC = 'Lieutenant Governor in Council (by Order in Council)';
/** s.7.0.1(1): the Premier may declare in urgent circumstances (a Premier's urgent order is
 *  terminated after 72 hours unless confirmed by the LGIC — s.7.0.1(2)). */
export const PROVINCIAL_DECLARER_PREMIER = 'Premier (urgent circumstances)';
/** The Order-in-Council reference field (the provincial analogue of the BCR reference). */
export const OIC_REFERENCE_FIELD_LABEL = 'Order in Council (OIC) reference';
/** The explicit state when no OIC is recorded yet (a Premier's urgent order, before the OIC). */
export const OIC_TO_FOLLOW_LABEL = 'Order in Council to follow';
/** The declared-area field label + its default (verbatim s.7.0.1(1): "throughout Ontario or in
 *  any part of Ontario"). Stored in metadata.provincial_declared_area (no new column). */
export const PROVINCIAL_DECLARED_AREA_LABEL = 'Declared area';
export const PROVINCIAL_DECLARED_AREA_DEFAULT = 'throughout Ontario';
/** The distinct provincial affordance option (NOT a roster jurisdiction — a fixed sentinel). */
export const PROVINCIAL_DECLARE_OPTION_LABEL =
  'Province of Ontario — provincial declaration (EMCPA s.7.0.1)';

/** Lifecycle: 'declared' (in force) | 'terminated' (ended). */
export const DeclarationStatusSchema = z.enum(['declared', 'terminated']);
export type DeclarationStatus = z.infer<typeof DeclarationStatusSchema>;

/**
 * Operator-facing labels — doctrine-strict (per-surface rule §4.1: UI = strict verbatim).
 * "Declare emergency" puts "declare" in its CORRECT statutory home (§2.1). A public alert
 * is ISSUED only by the PEOC (PERP §6) — this surface NEVER says "issue / send / broadcast
 * public alert" (Slice 1 does not build the alert-request; that is Declaration Slice 2).
 * Pinned by emergencyDeclarationVocabulary.lock.test.ts (editing them is founder authorization).
 */
export const DECLARE_EMERGENCY_LABEL = 'Declare emergency';
export const DECLARATION_OF_EMERGENCY_HEADING = 'Declaration of Emergency';
export const TERMINATE_DECLARATION_LABEL = 'Terminate declaration of emergency';
export const NOTIFY_PEOC_LABEL = 'Notify the PEOC';

/**
 * Bill 25 (the re-enacted EMCPA s.4 post-termination report to the Minister) is DORMANT.
 * The columns exist (migration 0124) but the UI is Declaration Slice 4 (#528) — gated OFF
 * here. The report is authored BY the head of council, addressed TO the Minister (never
 * "a Minister's report").
 *
 * WHY IT IS `false` (records-not-confers): the *Emergency Management Modernization Act, 2025*,
 * S.O. 2025, c. 16 (Royal Assent 2025-12-03), Sched. 1 s. 7 re-enacts EMCPA s. 4 — but
 * Sched. 1 s. 29(2) defers Sched. 1 ss. 5–8 (s. 7 is within that range) to "a day to be named
 * by order of the Lieutenant Governor in Council." No such LGIC order is in force, so the
 * re-enacted s. 4 (incl. s.4(10) "Reporting to Minister" + s.4(6) "Minister … promptly
 * notified") is NOT law today. Flipping this flip-gates BOTH the s.4(10) report UI AND the
 * s.4(3)→s.4(6) notify-copy swap (legally atomic — the same re-enacted s. 4).
 *
 * FLIP-GATE (do NOT flip casually — flipping to `true` requires ALL of):
 *   (1) a FOUNDER-SIGNED edit to this const AND its lock test (which pins
 *       `expect(BILL25_S4_IN_FORCE).toBe(false)`);
 *   (2) a FETCHED e-laws / LGIC-order citation showing the re-enacted EMCPA s. 4 is in force
 *       (Bill 25 = S.O. 2025, c. 16; Sched. 1 s. 29(2) is the commencement gate); AND
 *   (3) the prescribed-period regulation check — s.4(10) reads "within the prescribed period
 *       of time," which a regulation must fill before the duty is operable.
 * e-laws is a JS SPA (headless-walled), so the flip's in-force verification needs a real
 * browser session — it cannot be confirmed headlessly. Typed `: boolean` (not the literal
 * `false`) so the ON branches are genuinely reachable/type-checked, not dead-code-narrowed.
 */
export const BILL25_S4_IN_FORCE: boolean = false;

/**
 * The EMCPA s.4(3) notify-the-Minister copy the Notify-PEOC surface states TODAY (flag OFF) —
 * BOTH the statutory addressee AND the live, method-agnostic route. The PEOC Duty Officer is
 * the STABLE endpoint. This is the in-force s.4(3) copy while the re-enacted s.4 (Bill 25,
 * Sched. 1 s. 7) remains "not in force" (e-laws 90e09 s.4 amendment table).
 *
 * #530 (the "Solicitor General" → "the Minister" strike): S.O. 2025, c. 16, Sched. 1 s. 27
 * strikes "Solicitor General" → "Minister" GLOBALLY across the EMCPA and is IN FORCE at Royal
 * Assent 2025-12-03 (e-laws s.4 amendment table: "2025, c. 16, Sched. 1, s. 27 - 03/12/2025";
 * s. 27 is NOT in the deferred Sched. 1 s. 29(2) block, which defers ss. 5–8). This strike is
 * INDEPENDENT of the deferred s.4 re-enactment, so the in-force s.4(3) notify duty already
 * reads "the Minister" — "Solicitor General" now appears NOWHERE in the consolidated Act.
 * "Minister" = the Minister of Emergency Preparedness and Response (EMCPA s.1). Kept
 * province-scoped (Ontario), not hardcoded for a future multi-province cite. Verified
 * 2026-07-10 against the official e-Laws consolidation (currency 2026-07-07).
 */
export const DECLARATION_PEOC_NOTIFY_COPY = {
  statutory_addressee:
    'the Minister (statutory addressee, EMCPA s.4(3), as amended by S.O. 2025, c. 16 Sched. 1 s.27)',
  live_route:
    'routed to the Minister of Emergency Preparedness and Response via the PEOC Duty Officer',
} as const;

/**
 * The re-enacted EMCPA s.4(6) notify copy the Notify-PEOC surface states when Bill 25 s. 4 is
 * in force (flag ON) — the statutory basis flips from s.4(3) (Solicitor General) to s.4(6)
 * ("the head of council shall ensure that the Minister is promptly notified of a declaration
 * made under subsection (1) [declaring] or (5) [terminating]"), so it covers BOTH the
 * declaration AND its termination. NO "Solicitor General" (forbidden on the flag-ON surface).
 * Verbatim source: S.O. 2025, c. 16, Sched. 1 s. 7, re-enacted EMCPA s.4(6).
 */
export const DECLARATION_PEOC_NOTIFY_COPY_S4_6 = {
  statutory_addressee:
    'the Minister is promptly notified of the declaration and its termination (Minister of Emergency Preparedness and Response, EMCPA s.4(6))',
  live_route:
    'routed to the Minister of Emergency Preparedness and Response via the PEOC Duty Officer',
} as const;

/**
 * The full Notify-the-PEOC statutory surface — the ONE selector every consumer (the block
 * notify panel, the notify route payload, the PDF notification section) reads so the
 * s.4(3)→s.4(6) swap is legally ATOMIC with the report UI. Defaults to the module flag; the
 * `inForce` param exists ONLY so the lock test can pin BOTH states without flipping the flag.
 *   - `cite`               — the statutory citation ('s.4(3)' | 's.4(6)') for the checkbox /
 *                            PDF heading.
 *   - `statutory_addressee`/`live_route` — the addressee + method-agnostic route copy.
 */
export interface DeclarationNotifyStatutorySurface {
  cite: string;
  statutory_addressee: string;
  live_route: string;
}
export function declarationNotifyStatutorySurface(
  inForce: boolean = BILL25_S4_IN_FORCE,
): DeclarationNotifyStatutorySurface {
  return inForce
    ? { cite: 's.4(6)', ...DECLARATION_PEOC_NOTIFY_COPY_S4_6 }
    : { cite: 's.4(3)', ...DECLARATION_PEOC_NOTIFY_COPY };
}

/* ================================================================== *
 * DECLARATION Slice 4 (#528) — the Bill-25 DORMANT post-termination
 * report-to-the-Minister UI. GATED on BILL25_S4_IN_FORCE (today: false).
 *
 * STATUTORY GROUND (records-not-confers): the re-enacted EMCPA s.4(10)
 * "Reporting to Minister" (S.O. 2025, c. 16, Sched. 1 s. 7) — the head of
 * council of the municipality shall, "within the prescribed period of time
 * after the termination of the emergency, provide a written report to the
 * Minister respecting the emergency," which shall include (a) the date and
 * time the emergency was declared, (b) the date and time the termination was
 * declared, (c) an explanation, and (d) any other prescribed information. The
 * report is authored BY the head of council, addressed TO the Minister — NEVER
 * "a Minister's report", NEVER "submit/file/send" (the COP RECORDS it, it does
 * not submit it), NEVER a rendered numeric deadline ("within the prescribed
 * period of time" verbatim only). MUNICIPAL only (a First Nation is not subject
 * to the EMCPA — PERP §1.3.2). See docs/ontario-ims-doctrine-reference.md §4.3.
 * All strings pinned by emergency-declaration.lock.test.ts.
 * ================================================================== */

/** The s.4(10) sub-block heading (verbatim marginal heading of the re-enacted s.4(10)). */
export const DECLARATION_REPORT_HEADING = 'Reporting to Minister';
/** The report RECIPIENT — addressed TO the Minister (authorship is the head of council; reuse
 *  DECLARATION_DECLARER_ROLE.municipal for the AUTHOR row). Never "a Minister's report". */
export const DECLARATION_REPORT_RECIPIENT =
  'the Minister (Minister of Emergency Preparedness and Response)';
/** The record action — "Record", never "Submit / File / Send": the COP records the report on
 *  behalf of the head of council; it does not submit it. */
export const RECORD_REPORT_LABEL = 'Record report to the Minister';
/** The record-state line uses the statute's own verb ("provide … a … report"). */
export const DECLARATION_REPORT_PROVIDED_PREFIX = 'Provided';
export const DECLARATION_REPORT_NOT_PROVIDED_LABEL = 'Not yet provided';
/** The verbatim s.4(10) opening clause — NO digit-bearing deadline ("within the prescribed
 *  period of time" verbatim ONLY). Source: S.O. 2025, c. 16, Sched. 1 s. 7. */
export const DECLARATION_REPORT_S4_10_QUOTE =
  'The head of council of the municipality shall, within the prescribed period of time after the termination of the emergency, provide a written report to the Minister respecting the emergency.';
/** Helper copy — records-not-confers (the COP records the report; it does not submit it). */
export const DECLARATION_REPORT_HELPER =
  'The COP records the report on behalf of the head of council — it does not submit it.';
/** The optional static s.4(10)(a)–(d) checklist — VERBATIM (S.O. 2025, c. 16, Sched. 1 s. 7). */
export const DECLARATION_REPORT_CONTENTS_S4_10 = [
  'the date and time the emergency was declared;',
  'the date and time the termination of the emergency was declared;',
  'an explanation of why the head of council declared the emergency, including why the head of council was satisfied that the condition in clause (2) (b) was met; and',
  'any other prescribed information.',
] as const;
/** The durable author token written to post_termination_report_author_kind (the s.4(10) author
 *  is always the head of council; the rendered "Head of Council" label is its label form). */
export const DECLARATION_REPORT_AUTHOR_KIND = 'head_of_council';

/* ================================================================== *
 * PROVINCIAL closeout (#616, migration 0150) — EMCPA s.7.0.10, the
 * Premier's report to the Assembly. The RECIPROCAL of the municipal
 * s.4(10) closeout above — a DIFFERENT instrument with DISTINCT storage
 * (assembly_report_*). Shown when authority_kind='provincial' AND
 * status='terminated'. LIVE (do NOT flag-gate — s.7.0.10 is in-force EMCPA,
 * 2006, c. 13, s. 1(4); NOT Bill 25).
 *
 * GOVERNING SOURCE (records-not-confers): EMCPA s.7.0.10(1), verbatim
 * (e-laws 90e09; doctrine-reference §4.3): "The Premier shall table a report
 * in respect of the emergency in the Assembly within 120 days after the
 * termination of an emergency declared under section 7.0.1 and, if the
 * Assembly is not then in session, the Premier shall table the report within
 * seven days of the Assembly reconvening." The Premier TABLES it — the COP
 * RECORDS that it was tabled; it does NOT table / author / file it.
 *
 * ★ NEVER conflate with the municipal s.4(10) set: the provincial closeout
 * names "the Assembly" (never "the Minister"), the author is "the Premier"
 * (never "the head of council"), and the mark is a verbatim "120 days" (never
 * "the prescribed period"). Pinned by the reciprocal cross-guard in
 * emergency-declaration.lock.test.ts.
 *
 * ★ NEVER an "Overdue"/"Due"/"Deadline"/"Late" state: the COP does not hold
 * the Assembly's sitting calendar (the clock extends when the Assembly is not
 * in session), so it cannot compute the binding deadline — it renders a
 * "120-day mark" BESIDE the verbatim not-in-session limb, never asserting the
 * Premier breached a statutory duty.
 * ================================================================== */

/** The provincial-closeout sub-block heading. "the Assembly" is the statute's own word
 *  (s.7.0.10(1)), NOT "the Legislative Assembly". */
export const DECLARATION_ASSEMBLY_REPORT_HEADING =
  'Provincial closeout — report to the Assembly (EMCPA s.7.0.10)';
/** The report AUTHOR — the Premier (never "the head of council"). */
export const DECLARATION_ASSEMBLY_REPORT_AUTHOR = 'the Premier';
/** The report RECIPIENT / body — the Assembly (never "the Minister"). */
export const DECLARATION_ASSEMBLY_REPORT_RECIPIENT = 'the Assembly';
/** The record action — "Record", never "Table / Submit / File / Send": the Premier tables the
 *  report; the COP RECORDS that it was tabled — it does not table it. */
export const RECORD_ASSEMBLY_REPORT_LABEL = "Record the Premier's report to the Assembly";
/** The record-state prefix — rendered "Tabled <ts> (recorded in-COP)". Past tense: the Premier's
 *  completed act, recorded (not performed) by the COP. */
export const DECLARATION_ASSEMBLY_REPORT_TABLED_PREFIX = 'Tabled';
/** The suffix that keeps the tabling attributed to the Premier, only RECORDED by the COP. */
export const DECLARATION_ASSEMBLY_REPORT_RECORDED_SUFFIX = '(recorded in-COP)';
/** The not-yet state — "Not yet recorded in-COP" (NOT "Not yet provided": the COP only knows whether
 *  IT has recorded the tabling, never whether the Premier has discharged the duty). */
export const DECLARATION_ASSEMBLY_REPORT_NOT_RECORDED_LABEL = 'Not yet recorded in-COP';
/** The reference field label — a document tabled in the Assembly becomes an official Sessional Paper
 *  with a numerical catalogue number (Legislative Assembly of Ontario, ola.org). The provincial
 *  analogue of the OIC / BCR reference. */
export const ASSEMBLY_REPORT_REFERENCE_FIELD_LABEL = 'Sessional Paper number';
/** The 120-day-mark field label — a DERIVED display value (terminated_at + 120 days), NOT a binding
 *  deadline (see provincialCloseoutDeadlineMark). "mark", never "due"/"deadline". */
export const DECLARATION_ASSEMBLY_REPORT_DEADLINE_MARK_LABEL = '120-day mark';
/** The verbatim EMCPA s.7.0.10(1) quote — the "120 days" limb + the not-in-session 7-day limb ALWAYS
 *  shown together (never a rendered "due"/"overdue" state). Source: e-laws 90e09; 2006, c. 13, s. 1(4). */
export const DECLARATION_S7010_QUOTE =
  'The Premier shall table a report in respect of the emergency in the Assembly within 120 days after the termination of an emergency declared under section 7.0.1 and, if the Assembly is not then in session, the Premier shall table the report within seven days of the Assembly reconvening.';
/** Records-not-confers honesty line — the COP records that the Premier tabled the report; it does
 *  not author, table, or file it. */
export const DECLARATION_ASSEMBLY_REPORT_HELPER =
  'The COP records that the Premier tabled the report to the Assembly; it does not author, table, or file it.';
/** The PDF closeout-section title. The field set is DERIVED from the statute + real analogues (founder
 *  ruling 2026-07-11: do not wait for a government template), so the artifact is explicitly prefixed
 *  "Illustrative". */
export const DECLARATION_ASSEMBLY_REPORT_PDF_TITLE =
  'Illustrative provincial closeout record (EMCPA s.7.0.10)';
/** The s.7.0.10(2) PER-ORDER justification fields — a s.7.0.10 report is a per-ORDER justification of
 *  every emergency order made under s.7.0.2 / s.7.0.3 (NOT an after-action narrative). The COP does
 *  not hold the provincial orders, so these are the ILLUSTRATIVE STRUCTURE fields each order's entry
 *  carries — derived from s.7.0.10(2) → s.7.0.2(2) criteria / s.7.0.2(3) limitations / s.7.0.3(2).
 *  criteria = 7.0.2(2), limitations = 7.0.2(3) (the enacted cross-references). */
export const S7010_PER_ORDER_FIELDS = [
  'the order citation and the authority under which it was made (EMCPA s.7.0.2 or s.7.0.3);',
  'a description of the order;',
  'why the order was necessary and essential, and how it met the criteria in s.7.0.2(2);',
  'how the order satisfied the limitations in s.7.0.2(3);',
  'any amendments to the order; and',
  'the date the order was revoked or ceased to have effect.',
] as const;

/**
 * The read-time "120-day mark" for a provincial closeout (EMCPA s.7.0.10(1)) — 120 days after the
 * termination date. A DERIVED DISPLAY value only, NOT a binding deadline: the COP does not hold the
 * Assembly's sitting calendar (the statute extends the clock when the Assembly is not in session),
 * so it can never compute the true due date — it shows this mark BESIDE the verbatim not-in-session
 * limb, never a "due"/"overdue" state. Pure fn of terminated_at; returns YYYY-MM-DD (UTC) or null
 * when the declaration is not terminated / the date is unparseable. NOT a column, NOT a generated
 * column (timestamptz + interval is STABLE not IMMUTABLE — Postgres rejects it), NOT a cron.
 */
export function provincialCloseoutDeadlineMark(terminatedAtISO: string | null): string | null {
  if (!terminatedAtISO) return null;
  const d = new Date(terminatedAtISO);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + 120);
  return d.toISOString().slice(0, 10);
}

/**
 * The published Ontario PEOC 24/7 duty-officer toll-free line (ontario.ca, Fire Marshal
 * Communiqué 2023-1). Shown as the display default when a resolved destination carries no
 * contact_phone; the DB-seeded delivery_destinations.contact_phone (migration 0124) mirrors
 * this value and is what a notify actually returns.
 */
export const PEOC_DUTY_OFFICER_PHONE = '1-800-461-2281';

/* ================================================================== *
 * DECLARATION & NOTIFICATION Slice 3 (#517, migration 0126) — the
 * FIRST NATION declaration path + the PEOC / ISC dual notification.
 *
 * GOVERNING SOURCES (records-not-confers throughout):
 *  - PERP §6.6.4 — First Nations "can declare emergencies"; ISC "typically
 *    requires a band council resolution … but they may verbally declare an
 *    emergency … with a band council resolution to follow"; FNs "are advised to
 *    notify the PEOC" (ADVISED — never a statutory duty, so NEVER "as required").
 *  - PERP §6.7.1 — "First Nations requesting assistance from the PEOC should
 *    include ISC" (the dual fan-out: PEOC + ISC together).
 *  - PERP §1.3.2 — First Nations are NOT subject to the EMCPA (the municipal
 *    s.4(3) copy MUST NOT render on the FN path).
 *  - Indian Act, R.S.C. 1985, c. I-5, s.2(1)/s.2(3)(b) — the council acts
 *    COLLECTIVELY by resolution; attribution is the Band Council, never a single
 *    head (no "Chief declares" copy). Declarer role = the collective (already
 *    typed in DECLARATION_DECLARER_ROLE.first_nation).
 *
 * "Band Council Resolution / BCR" is CORRECT-DOMAIN-VERBATIM (First-Nations band
 * governance) — it clears the UI bar WITHOUT a §4.3 exception (doctrine-reference
 * §4.3). These strings are pinned by emergency-declaration.lock.test.ts.
 * ================================================================== */

/** The BCR capture field label (First-Nation path). */
export const BCR_FIELD_LABEL = 'Band Council Resolution (BCR)';
/** The explicit state when no BCR is recorded yet (PERP §6.6.4 verbal allowance). */
export const BCR_TO_FOLLOW_LABEL = 'Band Council Resolution to follow';
/** The FN person-name field. Attribution is COLLECTIVE (the Band Council by BCR),
 *  so any named person is only a SIGNATORY — never the declaring authority. */
export const FN_SIGNATORY_FIELD_LABEL = 'Signatory (optional)';

/** The FN dual fan-out control — ONE act, two GOVERNMENT legs (PERP §6.6.4 + §6.7).
 *  Never "issue / send / broadcast" (that is a public alert, PERP §6, Slice 2). */
export const FN_DUAL_NOTIFY_LABEL = 'Notify the PEOC and Indigenous Services Canada (ISC)';
/** Helper copy — records-not-confers + a public-alert misread guard. */
export const FN_DUAL_NOTIFY_HELPER =
  'notifies the PEOC and Indigenous Services Canada (government notification) — does not alert the public.';

/**
 * The authority-first record HEADLINE (misread guard, BOTH directions):
 *  - a First-Nation declaration never reads as a provincial declaration — the "(Band Council)"
 *    suffix carries the collective attribution;
 *  - a PROVINCIAL declaration reads "Province of Ontario" and never as a municipal/FN one — the
 *    headline is FIXED (EMCPA s.7.0.1 is always the Province of Ontario declaring, even when the
 *    declared AREA is a named part; the area is a record detail, not the declaring authority).
 * Rendered on the FN + provincial surfaces (block detail + PDF).
 */
export function declarationHeadline(
  kind: DeclarationAuthorityKind,
  authorityName: string,
): string {
  if (kind === 'first_nation') return `Declaration of Emergency — ${authorityName} (Band Council)`;
  if (kind === 'provincial') return 'Declaration of Emergency — Province of Ontario';
  return `Declaration of Emergency — ${authorityName}`;
}

/**
 * The EXACT dual-notify RECORD copy (records-not-confers; ADVISED not required, so
 * NEVER "as required"). Pinned verbatim by the lock test.
 */
export function fnDualNotifyRecordCopy(authorityName: string): string {
  return (
    `Notified the PEOC and Indigenous Services Canada (ISC) of the declaration of emergency by ` +
    `${authorityName} Band Council. Recorded in the COP on behalf of ${authorityName} — this record ` +
    `does not issue the declaration or confer any authority.`
  );
}

/* ------------------------------------------------------------------ *
 * Declare — POST /api/emergency-declarations
 * ------------------------------------------------------------------ */

export const EmergencyDeclarationCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** BOTH paths are wired: 'municipal' (Head of Council, Slice 1) and 'first_nation'
   *  (Band Council by BCR, Slice 3). */
  authority_kind: DeclarationAuthorityKindSchema.default('municipal'),
  /** The declaring jurisdiction — a REAL place (municipality / First Nation name), never
   *  a placeholder. */
  declaring_authority_name: z.string().min(1).max(200),
  /** The named person — the Head of Council (municipal) or an optional Signatory of the
   *  Band Council resolution (first_nation). Optional free text. */
  declarer_name: z.string().max(200).optional(),
  /** The Band Council Resolution reference (first_nation only; number + date free text).
   *  Omitted ⇒ "Band Council Resolution to follow" (PERP §6.6.4 verbal allowance). */
  bcr_reference: z.string().max(200).optional(),
  /** The Order-in-Council reference (provincial only; EMCPA s.7.0.1). Omitted on a Premier-urgent
   *  order ⇒ "Order in Council to follow". Ignored/nulled on municipal/FN (fenced route-side). */
  oic_reference: z.string().max(200).optional(),
  /** The provincial declared area (verbatim s.7.0.1(1): "throughout Ontario or in any part of
   *  Ontario"). Stored in metadata.provincial_declared_area; defaults to "throughout Ontario". */
  provincial_declared_area: z.string().max(200).optional(),
  /** Optional PARALLEL-SIBLING context link to the hazard/incident (never a precondition). */
  incident_id: UUID.optional(),
  hazard_type: z.string().max(80).optional(),
  notes: z.string().max(2000).optional(),
});
export type EmergencyDeclarationCreate = z.infer<typeof EmergencyDeclarationCreateSchema>;

/** POST /api/emergency-declarations/:id/terminate — flip declared → terminated. */
export const EmergencyDeclarationTerminateSchema = z.object({
  session_id: z.string().min(1).max(128),
  reason: z.string().max(2000).optional(),
  /** Optional terminating Band Council Resolution reference (first_nation) — a FN ends its
   *  declaration by a subsequent BCR. Folded into termination_reason (no extra column). */
  terminating_bcr_reference: z.string().max(200).optional(),
});
export type EmergencyDeclarationTerminate = z.infer<typeof EmergencyDeclarationTerminateSchema>;

/** PATCH /api/emergency-declarations/:id/bcr — backfill the Band Council Resolution reference
 *  later (PERP §6.6.4: a First Nation may verbally declare "with a band council resolution to
 *  follow"). First-Nation declarations only. */
export const EmergencyDeclarationBackfillBcrSchema = z.object({
  session_id: z.string().min(1).max(128),
  bcr_reference: z.string().min(1).max(200),
});
export type EmergencyDeclarationBackfillBcr = z.infer<typeof EmergencyDeclarationBackfillBcrSchema>;

/**
 * POST /api/emergency-declarations/:id/notify-peoc — the explicit, delegable "Notify the
 * PEOC" act. Routes through delivery.ts to the default PEOC destination (console_log /
 * download / email + a delivery_log row). call_confirmed + call_notes capture the operator
 * confirming they also placed the s.4(3) phone call (the call itself is the named scope-out).
 */
export const EmergencyDeclarationNotifyPeocSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** Explicit registry destination id; omitted → the default (PEOC) destination. */
  destination_id: UUID.optional(),
  /** The operator confirms the s.4(3) phone call to the PEOC Duty Officer was placed. */
  call_confirmed: z.boolean().optional().default(false),
  call_notes: z.string().max(2000).optional(),
});
export type EmergencyDeclarationNotifyPeoc = z.infer<typeof EmergencyDeclarationNotifyPeocSchema>;

/* ------------------------------------------------------------------ *
 * Read shape — a plain record (geography is optional; not served as GeoJSON in Slice 1)
 * ------------------------------------------------------------------ */

export const EmergencyDeclarationRecordSchema = z.object({
  id: z.string(),
  declaration_number: z.string(),
  authority_kind: DeclarationAuthorityKindSchema,
  declaring_authority_name: z.string(),
  declarer_name: z.string().nullable(),
  /** Band Council Resolution reference (first_nation). NULL ⇒ "to follow". */
  bcr_reference: z.string().nullable(),
  /** Order-in-Council reference (provincial). NULL ⇒ "Order in Council to follow" (Premier-urgent). */
  oic_reference: z.string().nullable(),
  /** Provincial declared area (from metadata; "throughout Ontario" | a named part). NULL on
   *  municipal/FN. */
  provincial_declared_area: z.string().nullable(),
  incident_id: z.string().nullable(),
  hazard_type: z.string().nullable(),
  status: DeclarationStatusSchema,
  declared_at: z.string(),
  terminated_at: z.string().nullable(),
  termination_reason: z.string().nullable(),
  peoc_notified_at: z.string().nullable(),
  /** Set when the FN dual fan-out notifies Indigenous Services Canada (paired with PEOC). */
  isc_notified_at: z.string().nullable(),
  notes: z.string().nullable(),
  /** The recorder (the operator who captured it in-COP) — ORTHOGONAL to the declarer. */
  created_by_operator_id: z.string().nullable(),
  /* BILL-25 s.4(10) post-termination report (municipal; Slice 4, dormant UI gated on
   * BILL25_S4_IN_FORCE). The DURABLE columns behind the report sub-block — the author token
   * ('head_of_council'), the recipient (the Minister), and when the report was RECORDED in-COP
   * ("Provided <ts>" / "Not yet provided"). NULL on every FN record + until the report is
   * recorded. Data-reality: author→_author_kind · recipient→_recipient · provided-at→_submitted_at. */
  post_termination_report_author_kind: z.string().nullable(),
  post_termination_report_recipient: z.string().nullable(),
  post_termination_report_submitted_at: z.string().nullable(),
  /** #529 (re-enacted EMCPA s.4(10)(c)/(d)) — the report explanation + any other prescribed
   *  information. DORMANT (municipal; gated on BILL25_S4_IN_FORCE). NULL until recorded / on FN. */
  post_termination_report_explanation: z.string().nullable(),
  post_termination_report_prescribed_info: z.unknown().nullable(),
  /* #616 (EMCPA s.7.0.10) — the PROVINCIAL closeout (LIVE; provincial, NOT Bill-25-gated). When the
   * Premier's report to the Assembly was TABLED (recorded in-COP), the Sessional Paper number, and a
   * short summary. DISTINCT from the municipal s.4(10) set above. NULL on municipal/FN + until recorded. */
  assembly_report_tabled_at: z.string().nullable(),
  assembly_report_reference: z.string().nullable(),
  assembly_report_summary: z.string().nullable(),
});
export type EmergencyDeclarationRecord = z.infer<typeof EmergencyDeclarationRecordSchema>;

/** The Notify-PEOC response — the record + the delivery outcome the operator sees (municipal). */
export const EmergencyDeclarationNotifyResultSchema = z.object({
  declaration: EmergencyDeclarationRecordSchema,
  delivery_channel: z.enum(['webhook', 'email', 'console_log']),
  destination_name: z.string().nullable(),
  /** The PEOC Duty-Officer phone the operator should call (from the destination row). */
  contact_phone: z.string().nullable(),
});
export type EmergencyDeclarationNotifyResult = z.infer<
  typeof EmergencyDeclarationNotifyResultSchema
>;

/* ------------------------------------------------------------------ *
 * FN dual fan-out — POST /api/emergency-declarations/:id/notify-peoc-isc
 * ONE act, two GOVERNMENT legs (PERP §6.6.4 + §6.7). First-Nation only.
 * ------------------------------------------------------------------ */

export const EmergencyDeclarationNotifyFnSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** The operator confirms they also placed the notification call(s) — the phone calls
   *  themselves are the named scope-out (surfaced + logged, not driven in-app). */
  call_confirmed: z.boolean().optional().default(false),
  call_notes: z.string().max(2000).optional(),
});
export type EmergencyDeclarationNotifyFn = z.infer<typeof EmergencyDeclarationNotifyFnSchema>;

/** One government notification leg (PEOC or ISC). */
export const DeclarationNotifyLegSchema = z.object({
  addressee: z.enum(['peoc', 'isc']),
  destination_name: z.string().nullable(),
  delivery_channel: z.enum(['webhook', 'email', 'console_log']),
  /** The 24/7 line the operator can call (PEOC only today; ISC is null — phone parity is a gap). */
  contact_phone: z.string().nullable(),
});
export type DeclarationNotifyLeg = z.infer<typeof DeclarationNotifyLegSchema>;

/** The FN dual-notify response — the record, the exact record copy, and BOTH legs. */
export const EmergencyDeclarationDualNotifyResultSchema = z.object({
  declaration: EmergencyDeclarationRecordSchema,
  /** The exact record line (fnDualNotifyRecordCopy) the COP shows for this act. */
  record_copy: z.string(),
  legs: z.array(DeclarationNotifyLegSchema),
});
export type EmergencyDeclarationDualNotifyResult = z.infer<
  typeof EmergencyDeclarationDualNotifyResultSchema
>;

/* ------------------------------------------------------------------ *
 * Bill-25 s.4(10) post-termination report — POST /:id/record-report
 * MUNICIPAL only (422 on first_nation — PERP §1.3.2), POST-termination only.
 * The route is the LIVE plumbing behind the Slice-4 flag-gated UI: it stamps
 * post_termination_report_submitted_at (+ the author/recipient columns) and
 * writes the record_post_termination_report verb (migration 0127). The COP
 * RECORDS the report on behalf of the head of council — it does not submit it.
 * ------------------------------------------------------------------ */
export const EmergencyDeclarationRecordReportSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** #529 — the re-enacted EMCPA s.4(10)(c) explanation of why the head of council declared the
   *  emergency (incl. why satisfied the clause (2)(b) condition was met). Optional free text;
   *  DORMANT capture (the report UI is gated on BILL25_S4_IN_FORCE). */
  explanation: z.string().max(4000).optional(),
  /** #529 — the s.4(10)(d) "any other prescribed information" (jsonb; shape TBD by the eventual
   *  prescribing regulation — captured as a free object until then). */
  prescribed_info: z.record(z.unknown()).optional(),
});
export type EmergencyDeclarationRecordReport = z.infer<
  typeof EmergencyDeclarationRecordReportSchema
>;

/* ------------------------------------------------------------------ *
 * PROVINCIAL closeout — POST /:id/record-assembly-report (EMCPA s.7.0.10)
 * The RECIPROCAL of the municipal /record-report. PROVINCIAL only (422
 * provincial_only on municipal/FN), POST-termination only. The COP RECORDS
 * that the Premier tabled the report to the Assembly (records-not-confers —
 * it does not table/author/file it). Writes the DISTINCT assembly_report_*
 * columns (migration 0150); reuses the record_post_termination_report verb
 * with a payload discriminator (report_instrument='s.7.0.10',
 * recipient='the Assembly') — NO new verb (verbs stay 193).
 * ------------------------------------------------------------------ */
export const EmergencyDeclarationRecordAssemblyReportSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** The date the Premier TABLED the report in the Assembly (ISO 8601, with offset). Optional —
   *  when omitted, the moment recorded in-COP is used. The operator may record the ACTUAL tabling
   *  date (it can fall days/weeks after termination). */
  tabled_at: z.string().datetime({ offset: true }).optional(),
  /** The Sessional Paper number (provincial analogue of the OIC / BCR reference). Optional free text. */
  assembly_report_reference: z.string().max(200).optional(),
  /** A short operator summary of the tabled report. Optional free text. */
  assembly_report_summary: z.string().max(4000).optional(),
});
export type EmergencyDeclarationRecordAssemblyReport = z.infer<
  typeof EmergencyDeclarationRecordAssemblyReportSchema
>;
